/*
 * Copyright (c) 2023 Helvia Technologies PC <contact@helvia.io>
 */
import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useIsRestoring } from "@tanstack/react-query";

// components
import { ConfigProvider, Spin } from "antd";
import { AuthLayout, MainLayout } from "./components/Layouts";
import { Checkout } from "./screens/Checkout";
import { CheckoutReturn } from "./screens/CheckoutReturn";
import { Projects } from "./screens/Projects";
import { RegistrationScreen } from "@/screens/Auth/RegistrationScreen";
import { LoginScreen } from "@/screens/Auth/LoginScreen";
import { ProfileScreen } from "@/screens/ProfileScreen";
import { EmailConfirmationScreen } from "@/screens/Auth/EmailConfirmationScreen";
import PrivateRoute from "@/screens/Routes/PrivateRoute";
import PublicRoute from "@/screens/Routes/PublicRoute";
import { ForgotPasswordScreen } from "@/screens/Auth/ForgotPasswordScreen";
import { ResetPasswordScreen } from "@/screens/Auth/ResetPasswordScreen";
import { EditProfileScreen } from "./screens/EditProfileScreen";
import { ProjectSingleView } from "./screens/ProjectSingleView";
import { Subscriptions } from "./screens/Subscriptions";
import { SubscriptionPlans } from "./screens/SubscriptionPlans";

// misc
import {
  NAV_ROOT,
  NAV_LOGIN,
  NAV_EMAIL_CONFIRMATION,
  NAV_REGISTRATION,
  NAV_FORGOT_PASSWORD,
  NAV_PROJECTS,
  NAV_RESET_PASSWORD,
  NAV_PROFILE,
  NAV_EDIT_PROFILE,
  NAV_PROJECT_SINGLE_VIEW,
  NAV_SUBSCRIPTION_PLANS,
  NAV_CHECKOUT,
  NAV_SUBSCRIPTIONS,
  NAV_CHECKOUT_SESSION,
} from "./constants";
import { useRefreshToken, useUserInfo } from "@/api/user";
import { THEME_PROVIDER } from "@/utils/css";

// styles
import "@fontsource/ubuntu/400.css";
import "@fontsource/ubuntu/500.css";
import "@fontsource/ubuntu/700.css";
import "./styles/main.scss";
import { useWorkspacesList } from "./api/workspaces";
import replaceUrlParams from "./utils/string/replaceUrlParams";
import { NotFound404 } from "./screens/404";
import { tokens as Tokens } from "@/utils/tokens";
import { queryClient } from "./utils/queryClient";
import { ITokens } from "./interfaces/User.interface";

const storageUserKey = 'rb_user';
/**
 * Root component that handles the routes and render the appropriate one based on path
 * Screen components to be rendered are wrapped with MainLayout component.
 */
function App() {
  const tokensInstance = Tokens;
  const isRestoringCache = useIsRestoring();
  const { mutate: refreshTokenMutate, isLoading: isRefreshingToken } = useRefreshToken();

  const userAuth: ITokens | undefined = queryClient.getQueryData(["user-auth"]);

  const { access: accessToken, refresh: refreshToken } = userAuth || {};
  const { data: userData } = useUserInfo(tokensInstance.getTokenSub(accessToken) as string);

  const { data: workspacesList } = useWorkspacesList({
    enabled: !!accessToken,
  });
  const [workspaceId, setWorkspaceId] = useState<string>(workspacesList ? workspacesList[0]?.uuid : "");

  if (
    accessToken &&
    refreshToken &&
    tokensInstance.isTokenAboutToExpire(accessToken) &&
    !tokensInstance.isTokenExpired(refreshToken) &&
    !isRefreshingToken
  ) {
    // refresh token
    refreshTokenMutate();
  }

  useEffect(() => {
    if (!workspacesList || (workspacesList && workspacesList[0]?.uuid === workspaceId)) {
      return;
    }
    setWorkspaceId(workspacesList[0]?.uuid);
  }, [workspacesList, workspaceId]);

  useEffect(() => {
    if (!userData) {
      return;
    }
    localStorage.setItem(storageUserKey, (userData.first_name + userData.last_name).toLowerCase());
  }, [userData])

  const rootRedirect = () => {
    if (accessToken) {
      return replaceUrlParams(NAV_PROJECTS, {
        workspaceId: workspaceId || workspacesList?.[0]?.uuid as string,
      })
    }

    return localStorage.getItem(storageUserKey) ? NAV_LOGIN : NAV_REGISTRATION
  }

  return (
    <ConfigProvider theme={THEME_PROVIDER}>
      {isRestoringCache ? (
        <MainLayout>
          <div className="mt-5 text-center">
            <Spin />
          </div>
        </MainLayout>
       ) : (
        <Routes>
          <Route path={"*"} element={<PublicRoute layout={MainLayout} component={NotFound404} />} />
          <Route
            path={NAV_ROOT}
            element={
              <Navigate
                to={rootRedirect()}
                replace
              />
            }
          />
          <Route path={NAV_PROJECTS} element={<PrivateRoute layout={MainLayout} component={Projects} />} />
          <Route path={NAV_PROJECT_SINGLE_VIEW} element={<PrivateRoute layout={MainLayout} component={ProjectSingleView} />} />
          <Route path={NAV_SUBSCRIPTION_PLANS} element={<PrivateRoute layout={MainLayout} component={SubscriptionPlans} />} />
          <Route path={NAV_SUBSCRIPTIONS} element={<PrivateRoute layout={MainLayout} component={Subscriptions} />} />
          <Route path={NAV_CHECKOUT} element={<PrivateRoute layout={MainLayout} component={Checkout} />} />
          <Route path={NAV_CHECKOUT_SESSION} element={<PrivateRoute layout={MainLayout} component={CheckoutReturn} />} />
          <Route path={NAV_PROFILE} element={<PrivateRoute layout={MainLayout} component={ProfileScreen} />} />
          <Route path={NAV_EDIT_PROFILE} element={<PrivateRoute layout={MainLayout} component={EditProfileScreen} />} />

          <Route path={NAV_FORGOT_PASSWORD} element={<PublicRoute layout={AuthLayout} component={ForgotPasswordScreen} />} />
          <Route path={NAV_RESET_PASSWORD} element={<PublicRoute layout={AuthLayout} component={ResetPasswordScreen} />} />
          <Route path={NAV_LOGIN} element={<PublicRoute layout={AuthLayout} component={LoginScreen} />} />
          <Route path={NAV_EMAIL_CONFIRMATION} element={<PublicRoute layout={AuthLayout} component={EmailConfirmationScreen} />} />
          <Route path={NAV_REGISTRATION} element={<PublicRoute layout={AuthLayout} component={RegistrationScreen} />} />
        </Routes>
      )}
    </ConfigProvider>
  );
}

export default App;
